import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

import global, { registerActAsUnauthorisedHandler } from "@/global";
import { setActAsTokenGetter, clearActAsTokenGetter } from "./actAsStorage";

const ACT_AS_STORAGE_KEY_TOKEN = "act-as-token";
const ACT_AS_STORAGE_KEY_DISPLAY_NAME = "act-as-display-name";
const ACT_AS_STORAGE_KEY_CLINICIAN_ID = "act-as-clinician-id";
const ACT_AS_STORAGE_KEY_RECORD_ID = "act-as-record-id";
const REFRESH_BEFORE_EXPIRY_MS = 60 * 1000; // 60 seconds before expiry

export interface ActAsState {
  isActingAs: boolean;
  actAsToken: string | null;
  displayName: string | null;
  /** EDIS clinicianId (operator code) for re-auth calls to initiate-act-as */
  actAsClinicianId: string | null;
  /** Stable clinician record ID for unambiguous re-auth selection */
  actAsRecordId: string | null;
}

export interface ActAsContextType {
  actAsState: ActAsState;
  startActingAs: (token: string, displayName: string, actAsClinicianId: string, actAsRecordId: string) => void;
  stopActingAs: () => void;
  /** Call when ACT_AS_UNAUTHORISED is received from API */
  requestReauth: () => void;
  /** Whether to show the re-auth PIN modal (token invalid/expired) */
  showReauthModal: boolean;
  dismissReauthModal: () => void;
  /** Get current token (for header) - reads from sessionStorage so it works outside React */
  getStoredToken: () => string | null;
}

const ActAsContext = createContext<ActAsContextType | null>(null);

export const useActAs = () => {
  const context = useContext(ActAsContext);
  if (!context) {
    throw new Error("useActAs must be used within an ActAsProvider");
  }
  return context;
};

/** Decode JWT payload without verification (client-side only to read exp) */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

function loadStoredState(): ActAsState {
  const token = sessionStorage.getItem(ACT_AS_STORAGE_KEY_TOKEN);
  const displayName = sessionStorage.getItem(ACT_AS_STORAGE_KEY_DISPLAY_NAME);
  const actAsClinicianId = sessionStorage.getItem(ACT_AS_STORAGE_KEY_CLINICIAN_ID);
  const actAsRecordId = sessionStorage.getItem(ACT_AS_STORAGE_KEY_RECORD_ID);
  return {
    isActingAs: !!token,
    actAsToken: token,
    displayName,
    actAsClinicianId,
    actAsRecordId,
  };
}

export const ActAsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [actAsState, setActAsState] = useState<ActAsState>(loadStoredState);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistState = useCallback((
    token: string | null,
    displayName: string | null,
    actAsClinicianId: string | null,
    actAsRecordId: string | null
  ) => {
    if (token) {
      sessionStorage.setItem(ACT_AS_STORAGE_KEY_TOKEN, token);
      if (displayName) sessionStorage.setItem(ACT_AS_STORAGE_KEY_DISPLAY_NAME, displayName);
      if (actAsClinicianId) sessionStorage.setItem(ACT_AS_STORAGE_KEY_CLINICIAN_ID, actAsClinicianId);
      if (actAsRecordId) sessionStorage.setItem(ACT_AS_STORAGE_KEY_RECORD_ID, actAsRecordId);
    } else {
      sessionStorage.removeItem(ACT_AS_STORAGE_KEY_TOKEN);
      sessionStorage.removeItem(ACT_AS_STORAGE_KEY_DISPLAY_NAME);
      sessionStorage.removeItem(ACT_AS_STORAGE_KEY_CLINICIAN_ID);
      sessionStorage.removeItem(ACT_AS_STORAGE_KEY_RECORD_ID);
    }
    setActAsState({
      isActingAs: !!token,
      actAsToken: token,
      displayName,
      actAsClinicianId,
      actAsRecordId,
    });
  }, []);

  const startActingAs = useCallback(
    (token: string, displayName: string, actAsClinicianId: string, actAsRecordId: string) => {
      persistState(token, displayName, actAsClinicianId, actAsRecordId);
      setShowReauthModal(false);
    },
    [persistState]
  );

  const stopActingAs = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    persistState(null, null, null, null);
    setShowReauthModal(false);
  }, [persistState]);

  const requestReauth = useCallback(() => {
    setShowReauthModal(true);
  }, []);

  const dismissReauthModal = useCallback(() => {
    setShowReauthModal(false);
  }, []);

  const getStoredToken = useCallback(() => {
    return sessionStorage.getItem(ACT_AS_STORAGE_KEY_TOKEN);
  }, []);

  // Keep actAsStorage getter in sync so global.ts getAuthHeaders can read token
  useEffect(() => {
    setActAsTokenGetter(() => sessionStorage.getItem(ACT_AS_STORAGE_KEY_TOKEN));
    return () => clearActAsTokenGetter();
  }, []);

  // When API returns act-as/unauthorised, show re-auth modal
  useEffect(() => {
    registerActAsUnauthorisedHandler(requestReauth);
    return () => registerActAsUnauthorisedHandler(null);
  }, [requestReauth]);

  const tryRefreshToken = useCallback(() => {
    const token = sessionStorage.getItem(ACT_AS_STORAGE_KEY_TOKEN);
    if (!token) return;
    global.client
      .post("meta/custom/refresh-act-as", { token })
      .then((res: unknown) => {
        const newToken = (res as { data?: { token?: string } })?.data?.token;
        if (newToken) {
          persistState(newToken, actAsState.displayName, actAsState.actAsClinicianId, actAsState.actAsRecordId);
        } else {
          requestReauth();
        }
      })
      .catch(() => requestReauth());
  }, [persistState, actAsState.displayName, actAsState.actAsClinicianId, actAsState.actAsRecordId, requestReauth]);

  // Schedule refresh ~60s before expiry
  const scheduleRefresh = useCallback(() => {
    const token = actAsState.actAsToken;
    if (!token) return;

    const payload = decodeJwtPayload(token);
    const exp = payload?.exp;
    if (!exp) return;

    const expiresAtMs = exp * 1000;
    const nowMs = Date.now();
    const refreshAtMs = expiresAtMs - REFRESH_BEFORE_EXPIRY_MS;

    if (refreshAtMs <= nowMs) {
      // Already past refresh time - try refresh once, else re-auth
      tryRefreshToken();
      return;
    }

    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      tryRefreshToken();
    }, refreshAtMs - nowMs);
  }, [actAsState.actAsToken, requestReauth, tryRefreshToken]);

  useEffect(() => {
    if (!actAsState.isActingAs) return;
    scheduleRefresh();
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [actAsState.isActingAs, actAsState.actAsToken, scheduleRefresh]);

  // Refresh on window focus when acting as
  useEffect(() => {
    if (!actAsState.isActingAs) return;

    const onFocus = () => {
      scheduleRefresh();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [actAsState.isActingAs, scheduleRefresh]);

  const value: ActAsContextType = {
    actAsState,
    startActingAs,
    stopActingAs,
    requestReauth,
    showReauthModal,
    dismissReauthModal,
    getStoredToken,
  };

  return (
    <ActAsContext.Provider value={value}>
      {children}
    </ActAsContext.Provider>
  );
};
